import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useFrame } from "@react-three/fiber"
import { useGLTF } from "@react-three/drei"
import { Color, Matrix4, Vector3 } from 'three'
import getTextPositions from "../Utils/TextPosition"
import getImagePositions from "../Utils/ImagePosition"
import getModelPositions from "../Utils/ModelPosition"

export default function Particles({ text, img, model }) {
    const [canvasDim, setCanvasDim] = useState({ width: 0, height: 0 })
    const isMobile = useMemo(() => window.innerWidth < 500, [canvasDim.width])
    const fontSize = isMobile ? 15 : 20
    const instanceCount = 25000
    const meshRef = useRef()
    const { scene } = useGLTF(model || './lily/scene.gltf')

    useEffect(() => {
        function updateCanvasDim() {
            const width = window.innerWidth / 2 - 70
            const height = window.innerHeight / 2 - 70
            setCanvasDim({ width: width > 400 ? 400 : width, height })
        }
        updateCanvasDim()
        window.addEventListener('resize', updateCanvasDim)
        return () => window.removeEventListener('resize', updateCanvasDim)
    }, [])

    const positions = useMemo(() => {
        const temp = []
        const getRandomCoord = () => Math.random() * 2000 - 1000
        for (let i = 0; i < instanceCount; i++) {
            temp.push(new Vector3(getRandomCoord(), getRandomCoord(), getRandomCoord()))
        }
        return temp
    }, [])

    const initialPositions = useMemo(() => positions.map(p => p.clone()), [])

    const [targetPositions, alpha] = useMemo(() => {
        if (model) {
            const dataArr = getModelPositions(scene, canvasDim.height)
            return [dataArr, Array(dataArr.length).fill(1)]
        }
        if (img) {
            const dataArr = getImagePositions(img, img.width, img.height)
            return [dataArr.map(p => new Vector3(p.x, p.y, p.z)), dataArr.map(p => p.w)]
        }
        if (text) {
            const dataArr = getTextPositions(text, fontSize, canvasDim.width, canvasDim.height)
            return [dataArr, Array(dataArr.length).fill(1)]
        }
        return [[], []]
    }, [text, img, model, scene])


    const matrix = useMemo(() => new Matrix4(), [])

    useLayoutEffect(() => {
        for (let i = 0; i < instanceCount; i++) {
            matrix.setPosition(positions[i])
            meshRef.current.setMatrixAt(i, matrix)
        }
        meshRef.current.instanceMatrix.needsUpdate = true
    }, [])

    const color = useMemo(() => new Color(), [])

    useFrame(() => {
        if (meshRef.current && targetPositions.length < instanceCount) {

            for (let i = 0; i < targetPositions.length; i++) {
                positions[i].lerp(targetPositions[i], img || model ? 0.015 : 0.05)
                matrix.setPosition(positions[i])
                meshRef.current.setMatrixAt(i, matrix)
                meshRef.current.setColorAt(i, color.setRGB(alpha[i], alpha[i], alpha[i]))
            }

            for (let i = targetPositions.length; i < instanceCount; i++) {
                positions[i].lerp(initialPositions[i], 0.005)
                matrix.setPosition(positions[i])
                meshRef.current.setMatrixAt(i, matrix)
                meshRef.current.setColorAt(i, color.setRGB(1, 1, 1))
            }

            meshRef.current.instanceMatrix.needsUpdate = true
            meshRef.current.instanceColor.needsUpdate = true
        }
    })

    return (
        <instancedMesh ref={meshRef} args={[null, null, instanceCount]}>
            <sphereGeometry args={isMobile ? [0.35, 8, 8] : [0.25, 8, 8]} />
            <meshBasicMaterial color='white' />
        </instancedMesh>
    )
}